import type { AutomationRunResultsDto } from '@tms/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api-client';
import { queryKeys } from '../../../lib/query-keys';
import { automationRequest } from '../client';
import { pipelineIdOf } from '../hooks';
import type { PoolAccountSummary } from '../testrun-types';

/**
 * What the run pages need beyond the shared hooks (features/automation/hooks.ts).
 */

/**
 * A run's results the way TestRun polled them: every 15 s while the run goes
 * (the report grows as classes finish); after it ends, until the report is
 * complete — at most six more reads, since GitLab may never build one.
 * The shared useRunResults stops at the first ready report, which for a live
 * run is a partial one.
 */
export const useLiveRunResults = (
  runId: string,
  {
    projectHint,
    against,
    final,
    enabled,
  }: { projectHint?: string; against?: string; final: boolean; enabled: boolean },
) => {
  const pipelineId = pipelineIdOf(runId);
  return useQuery({
    queryKey: queryKeys.automation.runPart(
      pipelineId,
      'results',
      `${against ?? ''}|${final ? 'final' : 'live'}`,
    ),
    queryFn: ({ signal }) =>
      api.get<AutomationRunResultsDto>(
        `/automation/runs/${pipelineId}/results`,
        {
          ...(projectHint ? { project: projectHint } : {}),
          ...(against ? { against } : {}),
        },
        signal,
      ),
    enabled,
    refetchInterval: (query) => {
      if (!final) return 15_000;
      const data = query.state.data;
      if (data?.ready && data.final) return false;
      return query.state.dataUpdateCount < 6 ? 10_000 : false;
    },
    staleTime: final ? Infinity : 0,
  });
};

/**
 * Re-checks pool accounts on the stand one by one, as «check all» does — the
 * stand limits logins. Unlike useRecheckAccount it does not re-read the pool
 * after each account: the caller re-reads it once at the end. Returns the
 * accounts whose check failed, with the error.
 */
export const recheckInTurn = async (
  queryClient: QueryClient,
  accounts: PoolAccountSummary[],
  onProgress: (done: number) => void,
): Promise<Map<string, unknown>> => {
  const failed = new Map<string, unknown>();
  for (const [index, account] of accounts.entries()) {
    try {
      await automationRequest<PoolAccountSummary>(
        queryClient,
        `/accounts/${encodeURIComponent(account.id)}/check`,
        { method: 'POST', body: {} },
      );
    } catch (error) {
      failed.set(account.id, error);
    }
    onProgress(index + 1);
  }
  return failed;
};
